import type { ConstructorValue, BrandPalette } from '../../features/design-constructor';
import { DEFAULT_TEMPLATE, TEMPLATES, type Template } from './types';

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/**
 * Coerce a raw `template` string into the Template union. Anything outside
 * the whitelist falls back to DEFAULT_TEMPLATE ('classic'). Pure.
 */
export function normalizeTemplate(raw: unknown): Template {
  return typeof raw === 'string' && (TEMPLATES as readonly string[]).includes(raw)
    ? (raw as Template)
    : DEFAULT_TEMPLATE;
}

function hexOrNull(raw: string | null): string | null {
  if (!raw) return null;
  return HEX_RE.test(raw) ? raw : null;
}

// URL query-param → palette role. We keep the legacy v1 query names
// (button_color, bg_gradient_from, …) as a dev-only sidedoor because URL
// dot-notation is awkward; internally they map to v2 palette keys.
const URL_PARAM_TO_PALETTE_KEY: Record<string, keyof BrandPalette> = {
  button_color: 'primary',
  button_text_color: 'textOnBrand',
  accent_color: 'accent',
  border_color: 'surfaceBorder',
  bg_gradient_from: 'heroFrom',
  bg_gradient_to: 'heroTo',
  text_color: 'textPrimary',
};

export interface UrlThemeResult {
  /** null = template param absent → caller keeps its current template. */
  template: Template | null;
  /** null = no palette params present (and so no ConstructorValue to apply). */
  value: ConstructorValue | null;
}

/**
 * Parse theme overrides from URL query params (dev-only fallback channel).
 * Backend payload takes precedence — this is layered underneath in
 * ThemeProvider's initial-resolve.
 */
export function parseUrlTheme(url: string): UrlThemeResult {
  const params = new URL(url).searchParams;
  const tParam = params.get('template');
  const template = tParam ? normalizeTemplate(tParam) : null;

  const palette: Partial<BrandPalette> = {};
  for (const [param, key] of Object.entries(URL_PARAM_TO_PALETTE_KEY)) {
    const hex = hexOrNull(params.get(param));
    if (hex) palette[key] = hex;
  }

  const value: ConstructorValue | null =
    Object.keys(palette).length > 0
      ? { version: 2, palette: palette as BrandPalette }
      : null;

  return { template, value };
}
